import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { LL } from 'i18n/i18n.service';
import { CanLeaveResult, CsPage, NavController } from 'providers/nav-controller';
import { UserDb } from 'services/user-db.service';
import { Exercise, ExerciseDbService, exerciseUsesRMTesting, getReadableSlotNameFromId } from 'services/exercise-db.service';
import { subSlotUsesMyoReps } from '@cf-shared/data-model/exercise-myoreps';
import { DebugService } from 'services/debug.service';
import { DatabaseWrapper , DocWatcher } from 'services/database-wrapper';
import { FitnessPlan, Plan_SubSlot, Plan_Workout, emptyWorkoutsAreAllAtTheEnd, findEmptyWorkouts } from '@cf-shared/data-model/fitness-plan';
import { DeclaredState, FitnessState, ImmutableState, getWorkoutsPerWeek } from '@cf-shared/data-model/user-state';
import { UiExercise } from 'models/workout-ui-models';
import { addExerciseToWorkoutAsync, changeExerciseInWorkoutAsync, ExerciseChosenBy, getExerciseForSubSlot } from 'services/exercise-switching';
import { restoreTrainingProgram } from 'services/program-backup-restore';
import { CoachExerciseChangeService, RecentExerciseHistory } from 'services/coach-exercise-change.service';
import { applyPreparedChange, bodyWeightShareFromSessions, prepareChange } from 'services/coach-exercise-change';
import { ExerciseChangeDoc } from '@cf-shared/data-model/exercise-change';
import { setAsideExerciseIdOf } from '@cf-shared/data-model/fitness-plan';
import { EditWorkoutsPageParams, EditWorkoutsPageResult } from './edit-workouts-interface';
import { CouchDocDraft, deepClone, deepCompareEQ, deepOverwriteCouchSafe } from '@cf-shared/deep-helpers';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { ToastController } from 'providers/toast-controller';
import { UnsavedChangesGuard, UnsavedChangesGuardProvider } from 'providers/unsaved-changes-guard/unsaved-changes-guard';
import { PopupMenuProvider, PopupMenuButton } from 'providers/popup-menu/popup-menu';
import { IExerciseStatsPage } from 'pages/exercise-stats/exercise-stats-interface';
import { IExerciseSwitchPage } from 'pages/exercise-switch/exercise-switch-interface';
import { IExerciseConfigurationPage, ExerciseConfigurationPageResult } from 'pages/exercise-configuration/exercise-configuration-interface';
import { SettingsService } from 'services/settings.service';
import { hasClientSmallScreen } from 'environments/environment-helpers';
import { MuscleVolumeEntry } from 'services/workout-loader.service';
import { planRepTargetText, planSetCount } from 'util/exercise-plan-texts';
import { buildMuscleVolumeEntries, buildMuscleVolumeInputsAsync } from 'util/muscle-volume-helpers';
import { moveSubslotToWorkout, removeSubslotFromWorkout } from 'util/orphan-subslot-cleanup';
import { IMuscleVolumePage, MuscleVolumePageResult } from 'pages/muscle-volume-page/muscle-volume-page-interface';
import { IWorkoutPlanScannerPage, WorkoutPlanScannerPageResult } from 'pages/workout-plan-scanner-page/workout-plan-scanner-page-interface';
import { applyScannedProgramToDraftAsync, WorkoutPlanApplyMode } from 'pages/workout-plan-scanner-page/workout-plan-scanner-apply';
import { ResolvedProgram } from 'pages/workout-plan-scanner-page/workout-plan-scanner-mapping';
import { AuthService } from 'services/auth.service';
import { SvPlanHandlerService } from 'services/server/sv-plan-handler.service';
import { TelemetryService } from 'services/telemetry.service';
import { LoggingService } from 'services/logging.service';
import { isDatabaseTornDown } from 'services/database-unavailable-error';
import { describeErrorForLog } from '@cf-shared/error-describe';

@Component({
  selector: 'page-edit-workouts',
  templateUrl: 'edit-workouts-page.html',
  styleUrls: ['edit-workouts-page.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditWorkoutsPage extends CsPage<EditWorkoutsPageParams, EditWorkoutsPageResult> {
  protected LL = LL;
  protected loading = true;
  protected fitnessPlan: FitnessPlan; // working copy for UI modifications
  protected fitnessState: FitnessState; // working copy for UI modifications
  protected declaredState: DeclaredState;
  private immutableState: ImmutableState;
  hasClientSmallScreen = hasClientSmallScreen;
  // Always read the live array rather than caching it in a field. A field went stale the
  // moment a handler replaced the plan's contents wholesale — deepOverwriteCouchSafe (see
  // onConfigurationResult) deletes every key on the plan and re-assigns deep COPIES, so
  // fitnessPlan.workouts becomes a different array holding different workout objects. The
  // page then edited workouts that were no longer in the plan it saves: every structural
  // edit was silently dropped, and an exercise swap was refused with no message because
  // fitnessPlan.workouts.indexOf(workout) answered -1 (#4689).
  protected get workouts(): Plan_Workout[] { return this.fitnessPlan?.workouts ?? this.workoutsBeforePlanLoaded; }

  // What the getter above answers with while there is no plan yet — during the two awaited
  // database reads in loadAsync, and for a plan that carries no workouts array at all. It is
  // one field rather than a fresh [] per call so the getter returns the SAME reference every
  // time, which is what stops the template's @for re-rendering on every change-detection pass.
  // It is deliberately PER INSTANCE and not a module-level constant: the "+" tab lives in the
  // page header, outside the region the loading spinner covers, so it can be tapped while the
  // plan is still loading — and addNewWorkout then pushes into whatever this getter answered.
  // Per instance, such a push is discarded with the page, exactly as it was before `workouts`
  // became a getter. Shared, it would survive into every later Edit Workouts instance and draw
  // phantom workout tabs there.
  private readonly workoutsBeforePlanLoaded: Plan_Workout[] = [];
  protected selectedWorkoutName: string | 'add';
  protected workoutExercises: Map<string, UiExercise[]> = new Map();
  // true while the exercise rows could not be built because an exercise could not be read
  // (a timed-out catalog fetch, say). The rows are then hidden behind a Try Again card, and
  // Save rebuilds them first, so a stale or partial row list can never feed a reorder or the
  // save's combo-set groups (df-a89f1ea2145e)
  protected exercisesLoadFailed = false;
  protected lowerBodyMuscles: MuscleVolumeEntry[] = [];
  protected upperBodyMuscles: MuscleVolumeEntry[] = [];
  // #3790: subSlotIds the volume calculator could not attribute to any muscle (e.g. custom
  // exercises with no "Most Similar Exercise" set). Passed to the muscle-volume panel so it can
  // tell the user some exercises are not counted.
  protected uncountedSubSlotIds: string[] = [];

  // mode: 'intake' uses passed-in data, 'edit' (default) loads from database
  private mode: 'intake' | 'edit' = 'edit';

  private docWatcher: DocWatcher;
  // CouchDocDraft wrappers: hold canonical reference + editable clone
  private fitnessPlanDraft: CouchDocDraft<FitnessPlan>;
  private fitnessStateDraft: CouchDocDraft<FitnessState>;

  // snapshots for change detection
  private fitnessPlanOrg: FitnessPlan;
  private fitnessStateOrg: FitnessState;

  /**
   * #6428: the program entries the exercise settings page changed with its coach's parts shown, and
   * whether a scheme switch built their paths. Only these get the shared save step (the deload
   * rule, the stamp, the change record) at Save; an ordinary user's edit is never in here.
   */
  private coachEdits = new Map<string, { pathsRebuiltBySwitch: boolean; volume?: { before: number; after: number } }>();

  private unsavedChangesGuard: UnsavedChangesGuard<EditWorkoutsPageResult>;
  // tracks whether user exited via save button (vs back button) for telemetry
  private exitViaSave = false;

  constructor(
    public navCtrl: NavController,
    protected userDb: UserDb,
    protected exerciseDb: ExerciseDbService,
    protected debug: DebugService,

    private cdr: ChangeDetectorRef,
    private alertCtrl: AlertController,
    private popupMenu: PopupMenuProvider,
    private unsavedChangesGuardProvider: UnsavedChangesGuardProvider,
    private toastCtrl: ToastController,
    private settings: SettingsService,
    private svPlanHandler: SvPlanHandlerService,
    private telemetry: TelemetryService,
    private logging: LoggingService,
    private auth: AuthService,
    private coachExerciseChange: CoachExerciseChangeService,
  ) {
    super();
  }

  async ionViewWillEnter() {
    if (!this.docWatcher) {
      this.docWatcher = this.userDb.database.createDocWatcher((_changes) => {
        if (this.viewDestroyed) return;
        this.cdr.markForCheck();
      });
    }

    // First entry: full load.
    if (!this.fitnessPlan) {
      await this.loadAsync();
      return;
    }

    // Re-entry from a child page (e.g. exercise-switch picker → edit-exercise fork-on-edit):
    // local saves in child pages don't fire the docWatcher (DatabaseWrapper's realtime-changes
    // handler short-circuits on `doc._rev !== cachedEntry.expectedRev` since local saves update
    // expectedRev to the saved rev synchronously — see design-docs/reactive-sync.md 'When
    // ionViewWillEnter is required as a resync point'). So our `fitnessPlan` draft can be
    // stale relative to the canonical that the child just mutated. Refresh from canonical
    // when the user has no pending edits — `hasUnsavedChanges` guards against stomping in-
    // flight user work. With unsaved changes we keep the draft as-is; deeper merge UX is a
    // separate follow-up (the draft/canonical merge problem isn't unique to this case).
    if (!this.hasUnsavedChanges()) {
      await this.loadAsync();
    }
  }

  async loadAsync() {
    this.loading = true;
    this.cdr.markForCheck();
    try {
      await this.loadPlanAndRowsAsync();
    } finally {
      // a read that failed must not leave the page on its spinner for ever
      this.loading = false;
      this.cdr.markForCheck();
    }
  }

  // reads the plan and states (or takes them from the intake), builds the guard, the rows and
  // the muscle-volume figures; loadAsync owns the spinner around it
  private async loadPlanAndRowsAsync() {
    // determine mode from params
    this.mode = this.navParams?.data?.mode || 'edit';

    if (this.mode === 'intake') {
      // intake mode: wrap passed-in objects
      this.fitnessPlanDraft = new CouchDocDraft(this.navParams.data.local?.fitnessPlan);
      this.fitnessStateDraft = new CouchDocDraft(this.navParams.data.local?.fitnessState);
      this.declaredState = this.navParams.data.local?.declaredState;
      this.immutableState = this.navParams.data.local?.immutableState;
    } else {
      // edit mode: load from database
      this.fitnessPlanDraft = new CouchDocDraft(await this.userDb.loadPlanAsync(this.docWatcher));
      this.fitnessStateDraft = new CouchDocDraft(await this.userDb.loadFitnessStateAsync(this.docWatcher));
    }

    // bind working copies to UI
    this.fitnessPlan = this.fitnessPlanDraft.draft;
    this.fitnessState = this.fitnessStateDraft.draft;
    // a fresh draft holds no coach edits yet
    this.coachEdits.clear();

    // load from DB if not provided via navParams (edit mode, or intake fallback)
    if (!this.declaredState) this.declaredState = await this.userDb.loadDeclaredStateAsync(this.docWatcher);
    if (!this.immutableState) this.immutableState = await this.userDb.loadImmutableStateAsync(this.docWatcher);

    // create snapshots for change detection
    this.fitnessPlanOrg = deepClone(this.fitnessPlan);
    this.fitnessStateOrg = deepClone(this.fitnessState);

    // create unsaved changes guard for ionViewCanLeave
    this.unsavedChangesGuard = this.unsavedChangesGuardProvider.createGuard<EditWorkoutsPageResult>(
      [this.fitnessPlan, this.fitnessState],
      // answering false tells the guard nothing was saved, so the user stays on the page
      async () => (await this.onSaveClick()) ? undefined : false,
      {
        // in intake mode, return the modified data to the caller via ionViewCanLeave result
        getResult: this.mode === 'intake'
          ? () => ({ fitnessPlan: this.fitnessPlan, fitnessState: this.fitnessState })
          : undefined,
      }
    );

    if (this.fitnessPlan?.workouts) {
      await this.reloadAllWorkoutExercisesAsync();

      // select initial workout: keep the user's current selection on re-entry reload if it
      // still exists; otherwise fall back to nav-params index, then to the first workout.
      const selectionStillValid = this.selectedWorkoutName
        && this.workouts.some(w => w.name === this.selectedWorkoutName);
      if (!selectionStillValid) {
        const initialIndex = this.navParams?.data?.workoutIndex ?? 0;
        if (this.workouts.length > initialIndex) {
          this.selectedWorkoutName = this.workouts[initialIndex].name;
        }
      }
    }

    await this.refreshMuscleVolumeAsync();
  }

  private async refreshMuscleVolumeAsync() {
    const inputs = await buildMuscleVolumeInputsAsync(this.fitnessPlan, this.declaredState, this.fitnessState, this.userDb, this.exerciseDb, this.docWatcher);
    const muscleVolume = buildMuscleVolumeEntries(this.fitnessPlan, this.declaredState, this.logging, inputs);
    this.lowerBodyMuscles = muscleVolume.lowerBodyMuscles;
    this.upperBodyMuscles = muscleVolume.upperBodyMuscles;
    this.uncountedSubSlotIds = muscleVolume.uncountedSubSlotIds;
  }

  /** Rebuild everything this page caches about the draft plan, after the draft has been
   *  changed. Call this — never the two refreshes below it by hand.
   *
   *  There are two caches and they are easy to get half-right, because forgetting either
   *  one is completely SILENT on this page:
   *
   *  - the exercise display rows (`workoutExercises`), whose text carries the set count.
   *    A row left unrebuilt shows the plan as it was, on this tab and on every other tab
   *    that lists the same sub-slot.
   *  - the muscle-volume figures (`lowerBodyMuscles` / `upperBodyMuscles` /
   *    `uncountedSubSlotIds`). This page's template binds NONE of them, so a stale figure
   *    is invisible here — it surfaces on the Muscle Volume page, which
   *    `handleViewMuscleVolume` seeds from these three fields as nav params.
   *
   *  Four sites in this file had already forgotten one of the two before this method
   *  existed (`df-2d4a0fbf18c4`, `df-5f3735969b6e`, `df-44aded267ee8`), which is why the
   *  pairing is written once here rather than at every call site. The order is part of it:
   *  rows first, then figures.
   *
   *  Deliberately NOT guarded by `viewDestroyed` between the two steps, although two callers
   *  used to be. That flag means the user has LEFT the page, not that it is gone — it is
   *  cleared again on re-entry, and re-entry only reloads the draft when it has no unsaved
   *  changes. So stopping half way would leave the figures stale for as long as the draft
   *  lives, which is the very thing this method exists to prevent. Neither step touches
   *  change detection, so there is nothing to protect against here.
   *
   *  Two sites deliberately do NOT call this, and both are correct as they are:
   *  - `loadAsync` is the page's initial build, not a plan mutation. Its row rebuild is
   *    conditional on the plan having workouts at all, and it owns the `loading` flag for
   *    the whole load.
   *  - `removeExerciseFromWorkout` drops the removed row from the affected workout's
   *    cached list in place, on purpose, so only the figures need rebuilding. */
  private async refreshAfterPlanChangeAsync() {
    await this.reloadAllWorkoutExercisesAsync();
    await this.refreshMuscleVolumeAsync();
  }

  /** Change the draft plan and rebuild both caches from it, with the loading spinner up for
   *  the whole thing. For a site that owns its mutation inline; a site whose mutation has
   *  already happened elsewhere (a return from another page, say) calls
   *  `refreshAfterPlanChangeAsync` on its own instead. */
  private async applyPlanChangeAsync(mutate: () => void | Promise<void>) {
    await this.withLoading(async () => {
      await mutate();
      await this.refreshAfterPlanChangeAsync();
    });
  }

  /** The header ⋮ overflow menu: Volume Optimization and Scan Workout. */
  async openHeaderMenu(event: Event) {
    const buttons: PopupMenuButton[] = [
      {
        text: LL.editWorkouts.menu.volumeOptimization(),
        icon: 'calculator',
        handler: () => this.handleViewMuscleVolume(),
      },
    ];
    buttons.push({
      text: LL.editWorkouts.menu.scanWorkout(),
      icon: 'camera',
      handler: () => this.openWorkoutPlanScanner(),
    });
    await this.popupMenu.createMenu({
      buttons,
      targetFromElement: event.currentTarget as HTMLElement,
    });
  }

  /** Push the workout plan scanner with the DRAFT plan/state — the scanner mutates the
   *  draft in place on Apply; nothing persists until this page's own Save. */
  openWorkoutPlanScanner() {
    void this.navCtrl.pushAsync(IWorkoutPlanScannerPage, {
      returnCallback: 'onWorkoutPlanScanResult',
      local: {
        fitnessPlan: this.fitnessPlan,
        fitnessState: this.fitnessState,
      },
    });
  }

  /** Return path from the scanner. The scanner only held a sessionStorage COPY of the
   *  draft (NavController `local` params are deep copies), so the apply happens HERE,
   *  on this page's live draft — then the workout tabs/exercise lists and the
   *  muscle-volume entries are rebuilt from it. */
  async onWorkoutPlanScanResult(_context: unknown, result: WorkoutPlanScannerPageResult) {
    // the scanner raised a global loading overlay before popping (so the spinner is up
    // during the back-transition); dismiss it once we are done, whatever the outcome
    try {
      if (!result?.applied || !result.program || !result.mode) return;
      await this.applyScannedProgramAsync(result.program, result.mode);
    } finally {
      await result?.loading?.dismiss();
    }
  }

  /** Apply a reviewed scanned program onto this page's LIVE draft, then rebuild the
   *  workout tabs / exercise lists / muscle-volume entries from it. */
  private async applyScannedProgramAsync(program: ResolvedProgram, mode: WorkoutPlanApplyMode) {
    // one-off watcher for the add-path's slot reads (see CLAUDE.md DocWatcher rule)
    try {
      await this.userDb.database.withDocWatcherAsync(watcher => applyScannedProgramToDraftAsync(
        program, mode, this.fitnessPlan, this.fitnessState,
        this.exerciseDb, this.userDb, this.auth.user.uid, this.debug.getNowLocal(), watcher,
      ));
    } catch (err) {
      // report BEFORE the view-destroyed guard — a failure during teardown must still
      // reach telemetry, even though no UI can be shown for it anymore
      this.telemetry.trackEvent('workout_plan_scan_error', { error: 'apply' });
      this.logging.trackAbnormalException(err);
      if (this.viewDestroyed) return;
      // the draft may be partially mutated — fall through to the rebuild below so the
      // UI shows the draft's actual state; canceling edit-workouts discards the draft
      await this.alertCtrl.showAlertAsync({
        title: LL.workoutPlanScanner.errorTitle(),
        message: LL.workoutPlanScanner.errorApply(),
        buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary }],
      });
      if (this.viewDestroyed) return;
    }

    await this.refreshAfterPlanChangeAsync();
    if (this.viewDestroyed) return;
    // replace mode may have removed the selected workout — fall back to the first one.
    // Safe to do after the rebuild above: neither refresh reads selectedWorkoutName.
    const selectionStillValid = this.selectedWorkoutName
      && this.workouts.some(w => w.name === this.selectedWorkoutName);
    if (!selectionStillValid && this.workouts.length > 0) {
      this.selectedWorkoutName = this.workouts[0].name;
    }
    this.cdr.markForCheck();
  }

  handleViewMuscleVolume() {
    void this.navCtrl.pushAsync(IMuscleVolumePage, {
      returnCallback: 'onMuscleVolumeResult',
      local: {
        lowerBodyMuscles: this.lowerBodyMuscles,
        upperBodyMuscles: this.upperBodyMuscles,
        uncountedSubSlotIds: this.uncountedSubSlotIds,
        reoptimize: {
          mode: 'draft',
          fitnessPlan: this.fitnessPlan,
          declaredState: this.declaredState,
          fitnessState: this.fitnessState,
          immutableState: this.immutableState,
        },
      },
    });
  }

  /** Return path from Muscle Volume. A re-optimise there hands back the set-count changes,
   *  which are applied HERE, on this page's live draft — then the exercise rows (whose text
   *  carries the set count) and the muscle-volume figures are rebuilt from it. The re-entry
   *  reload in ionViewWillEnter cannot do this: it runs before this callback, and is skipped
   *  entirely while the draft has unsaved edits. */
  async onMuscleVolumeResult(_context: unknown, result: MuscleVolumePageResult) {
    if (!(result?.appliedChanges?.length > 0)) return;
    this.svPlanHandler.applyVolumeChanges(this.fitnessPlan, result.appliedChanges);

    await this.refreshAfterPlanChangeAsync();
    if (this.viewDestroyed) return;
    this.cdr.markForCheck();
  }

  ngOnDestroy() {
    this.docWatcher?.destroy();
  }

  private async withLoading<T>(fn: () => Promise<T>): Promise<T> {
    this.loading = true;
    this.cdr.markForCheck();
    try {
      return await fn();
    } finally {
      this.loading = false;
      this.cdr.markForCheck();
    }
  }

  handleSwipeForward() {
    const i = this.workouts.findIndex(w => w.name === this.selectedWorkoutName);
    if (i >= 0 && i < this.workouts.length - 1) {
      this.onWorkoutSelected(this.workouts[i + 1].name);
    }
  }

  handleSwipeBack() {
    const i = this.workouts.findIndex(w => w.name === this.selectedWorkoutName);
    if (i > 0) {
      this.onWorkoutSelected(this.workouts[i - 1].name);
    }
  }

  onWorkoutSelected(workoutName: string | 'add') {
    if (workoutName === 'add') {
      this.addNewWorkout();
      return;
    }

    this.selectedWorkoutName = workoutName;
    this.cdr.markForCheck();
  }

  private addNewWorkout() {
    // generate next workout name (A, B, C, ...)
    const workoutNames = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const newName = workoutNames[this.workouts.length];

    // create new workout with empty exercises
    const newWorkout: Plan_Workout = {
      name: newName,
      subSlotIds: [],
    };

    // add to workouts array
    this.workouts.push(newWorkout);

    // initialize empty exercises cache
    this.workoutExercises.set(newName, []);

    // select the new workout
    this.selectedWorkoutName = newName;

    this.cdr.markForCheck();
  }

  /** Rebuild the cached display rows of every workout from the current plan, in parallel.
   *  All or nothing: the new rows replace the old ones only when every exercise was read. If
   *  one could not be read, the page shows its could-not-load card instead of any rows, and
   *  the failure is reported. Answers whether the rows were built. */
  private async reloadAllWorkoutExercisesAsync(): Promise<boolean> {
    try {
      const rows = await Promise.all(this.workouts.map(async w => [w.name, await this.buildExercisesForWorkoutAsync(w)] as const));
      this.workoutExercises = new Map(rows);
      this.exercisesLoadFailed = false;
      return true;
    } catch (err) {
      this.exercisesLoadFailed = true;
      // the app shutting its own database down (sign-out, say) is expected and not a fault;
      // every other failure is reported
      if (!isDatabaseTornDown(err)) {
        this.logging.trackSilentProblem('EditWorkoutsExerciseReadFailed', { error: describeErrorForLog(err) });
      }
      return false;
    }
  }

  /** The Try Again button on the could-not-load card. */
  async onRetryLoadExercisesClick() {
    await this.withLoading(() => this.refreshAfterPlanChangeAsync());
  }

  // builds one workout's display rows; throws when one of its exercises cannot be read
  private async buildExercisesForWorkoutAsync(workout: Plan_Workout): Promise<UiExercise[]> {
    if (!workout?.subSlotIds) {
      return [];
    }

    // load all exercises in parallel
    const exercisePromises = workout.subSlotIds.map(async (subSlotId) => {
      const subSlot = this.fitnessPlan.subSlots[subSlotId];
      if (!subSlot) return null;

      // get exercise info
      const exercise = await getExerciseForSubSlot(
        subSlot,
        subSlot.exercise_id,
        this.exerciseDb,
        this.userDb,
        this.fitnessState,
        this.fitnessPlan,
        this.debug.getNowLocal().toUTC(),
        this.docWatcher
      );

      if (!exercise) return null;

      // create simplified UiExercise for display
      const uiExercise = new UiExercise();
      uiExercise.exercise = exercise;
      uiExercise.planSubSlot = subSlot;
      // remember which sub-slot this row is, so nothing has to work it out from the object
      uiExercise.planSubSlotId = subSlotId;
      uiExercise.sets = [];

      // load combo set grouping from workout
      if (workout.comboSetGroups && workout.comboSetGroups[subSlotId] !== undefined) {
        uiExercise.comboSetNumber = workout.comboSetGroups[subSlotId];
      }

      // build edit mode description: "Slot Name | X Sets | RM Testing | Myo-Reps"
      uiExercise.editDescription = await this.buildEditDescriptionAsync(subSlot, exercise);

      return uiExercise;
    });

    const results = await Promise.all(exercisePromises);
    return results.filter((e): e is UiExercise => e !== null);
  }

  private async buildEditDescriptionAsync(subSlot: Plan_SubSlot, exercise: Exercise): Promise<string> {
    const parts: string[] = [];

    // get slot name (only if test setting is enabled)
    if (this.settings.showSlotName) {
      const slot = await this.exerciseDb.slotForIdAsync(subSlot.slotId, this.docWatcher);
      if (slot) {
        const slotName = getReadableSlotNameFromId(slot, subSlot.slotId);
        parts.push(slotName);
      }
    }

    // number of sets. the workout builder never applies a volume adjustment to a myo-rep
    // exercise, so neither does the count shown here — subSlotUsesMyoReps holds the rule
    // for asking that question, including which count it is asked with.
    const usesMyoReps = subSlotUsesMyoReps(subSlot, exercise, this.declaredState, this.fitnessState);
    const numSets = planSetCount(subSlot, exercise, this.declaredState, this.fitnessState);
    const setsText = LL.editWorkouts.setsCount({ count: numSets });
    parts.push(setsText);

    // rep target. the rule lives in util/exercise-plan-texts.ts, which the Training Overview reads too
    const repTargetText = planRepTargetText(subSlot, exercise, this.declaredState, this.fitnessState);
    if (repTargetText) {
      parts.push(repTargetText);
    }

    // RM testing
    if (exerciseUsesRMTesting(exercise, this.declaredState, this.fitnessState)) {
      parts.push(LL.editWorkouts.rmTesting());
    }

    // MyoReps
    if (usesMyoReps) {
      parts.push(LL.editWorkouts.myoReps());
    }

    return parts.join(' | ');
  }

  onExercisesReordered(workoutName: string, exercises: UiExercise[]) {
    this.workoutExercises.set(workoutName, exercises);
    // update workout subSlotIds order and comboSetGroups based on new exercise state
    const workout = this.workouts.find(w => w.name === workoutName);
    if (workout) {
      workout.subSlotIds = exercises
        .map(e => this.getSubSlotIdForExercise(e))
        .filter(Boolean);

      // update comboSetGroups immediately for change detection
      this.updateComboSetGroups(workout, exercises);
    }
    this.cdr.markForCheck();
  }

  private updateComboSetGroups(workout: Plan_Workout, exercises: UiExercise[]) {
    const comboSetGroups: { [subSlotId: string]: number } = {};
    let hasAnyComboGroups = false;

    for (const exercise of exercises) {
      if (exercise.comboSetNumber !== undefined && exercise.planSubSlot) {
        const subSlotId = this.getSubSlotIdForExercise(exercise);
        if (subSlotId) {
          comboSetGroups[subSlotId] = exercise.comboSetNumber;
          hasAnyComboGroups = true;
        }
      }
    }

    workout.comboSetGroups = hasAnyComboGroups ? comboSetGroups : undefined;
  }

  onInsertActionClick(workoutName: string, event: { insertAtIndex: number, action: string }) {
    if (event.action === 'exercise') {
      const workout = this.workouts.find(w => w.name === workoutName);
      if (!workout) return;

      const workoutReference = this.workouts.indexOf(workout);

      void this.navCtrl.pushAsync(IExerciseSwitchPage, {
        workoutId: workoutName,
        subSlotId: undefined, // adding new, not switching existing
        exerciseId: undefined, // no exercise selected yet
        returnCallback: 'onExerciseSwitched',
        warnBeforeLinkingWorkouts: true,
        local: {
          fitnessPlan: this.fitnessPlan,
          fitnessState: this.fitnessState,
          returnContext: {
            action: 'add',
            workoutName,
            workoutReference,
            insertAtIndex: event.insertAtIndex,
          },
        },
      });
    }
  }

  async onRevertClick() {
    if (this.mode === 'intake') {
      // intake mode: only undo session changes (no "Restore Original Plan")
      const confirmed = await this.alertCtrl.showAlertAsync<boolean>({
        title: LL.editWorkouts.revert.title(),
        message: LL.editWorkouts.revert.undoMessage(),
        buttons: [
          { text: LL.common.buttons.cancel(), role: AlertButtonRole.Cancel, result: false },
          { text: LL.editWorkouts.revert.undoButton(), role: AlertButtonRole.Primary, result: true },
        ],
      });

      if (confirmed) {
        await this.revertToSessionStart();
      }
      return;
    }

    // edit mode: show two revert options
    const result = await this.alertCtrl.showAlertAsync<'session' | 'original'>({
      title: LL.editWorkouts.revert.title(),
      message: LL.editWorkouts.revert.message(),
      buttons: [
        { text: LL.editWorkouts.revert.undoSession(), role: AlertButtonRole.Primary, result: 'session' },
        { text: LL.editWorkouts.revert.restoreOriginal(), result: 'original' },
        { text: LL.common.buttons.cancel(), role: AlertButtonRole.Cancel, result: undefined },
      ],
    });

    if (result === 'session') {
      await this.revertToSessionStart();
    } else if (result === 'original') {
      await this.restoreOriginalPlan();
    }
  }

  private async revertToSessionStart() {
    // if no changes were made, inform user
    if (!this.hasUnsavedChanges()) {
      await this.alertCtrl.showAlertAsync({
        title: LL.editWorkouts.revert.noChangesTitle(),
        message: LL.editWorkouts.revert.noChangesMessage(),
        buttons: [
          { text: LL.common.buttons.ok(), role: AlertButtonRole.Primary, result: true },
        ],
      });
      return;
    }

    // Restore from the session snapshots by overwriting the CONTENTS of the two objects the
    // page already holds, never by assigning a new object to the field. The page does not save
    // `fitnessPlan` directly: it saves through a CouchDocDraft, and that class captures the
    // object it will write once, in its constructor, into a readonly field that cannot be
    // repointed. So assigning a fresh clone here left the save path holding the pre-revert
    // object, and pressing Save afterwards wrote back exactly the changes the user had just
    // undone, with nothing reporting it (df-3b9aa71cd9d0). The same assignment also stranded
    // the unsaved-changes guard, which is built once in loadAsync from these two references.
    //
    // The overwrite replaces the whole plan's contents, so every cached display row would
    // still describe the plan the user just undid — its exercise, its description text and
    // the subSlot object it holds. applyPlanChangeAsync rebuilds them from the restored plan.
    await this.applyPlanChangeAsync(() => {
      deepOverwriteCouchSafe(this.fitnessPlan, this.fitnessPlanOrg);
      deepOverwriteCouchSafe(this.fitnessState, this.fitnessStateOrg);
    });

    // select first workout
    if (this.workouts.length > 0) {
      this.selectedWorkoutName = this.workouts[0].name;
    }
  }

  private async restoreOriginalPlan() {
    // check if backup exists
    if (!this.fitnessPlan.generatedPlanBackup) {
      await this.alertCtrl.showAlertAsync({
        title: LL.editWorkouts.revert.notAvailableTitle(),
        message: LL.editWorkouts.revert.notAvailableMessage(),
        buttons: [
          { text: LL.common.buttons.ok(), role: AlertButtonRole.Primary, result: true },
        ],
      });
      return;
    }

    // show confirmation dialog
    const confirmed = await this.alertCtrl.showAlertAsync({
      title: LL.editWorkouts.revert.confirmRestoreTitle(),
      message: LL.editWorkouts.revert.confirmRestoreMessage(),
      buttons: [
        { text: LL.common.buttons.cancel(), role: AlertButtonRole.Cancel, result: false },
        { text: LL.editWorkouts.revert.restoreButton(), role: AlertButtonRole.Primary, result: true },
      ],
    });

    if (!confirmed) return;

    // Restore from the generated backup. Go through restoreTrainingProgram rather than
    // cloning the backup straight over the plan: the backup is taken at build time AFTER the
    // rebuild has carried the previous program's training state onto the fresh slots, so a
    // bare clone would rewind every target weight and rep target the user has worked up to
    // since that build (#5113). `trainingStateOnly` says what this button means by "original
    // plan" — the exercise selection, the session structure and the set counts go back, and
    // the progression stays.
    // (applyPlanChangeAsync reloads the exercise rows and the volume figures from the
    // restored plan once the restore has run. The backup is read into a local first: the
    // guard above narrows it, and that narrowing does not reach inside the callback.)
    const generatedPlanBackup = this.fitnessPlan.generatedPlanBackup;
    await this.applyPlanChangeAsync(() => {
      restoreTrainingProgram(
        this.fitnessPlan,
        this.fitnessState,
        generatedPlanBackup,
        this.debug.getNowLocal(),
        'trainingStateOnly',
      );
    });
    // a restore is not a coach's change to an exercise; nothing from before it is recorded
    this.coachEdits.clear();

    // select first workout
    if (this.workouts.length > 0) {
      this.selectedWorkoutName = this.workouts[0].name;
    }
  }

  /** Save the edited program. Answers false when nothing was saved and the user stays. */
  async onSaveClick(): Promise<boolean> {
    // #6268: a day with no exercises must not be saved — the cycle would schedule it and
    // the Training page would show a blank workout
    if (!await this.resolveEmptyWorkoutsBeforeSaveAsync()) return false;

    // if the exercise rows could not be loaded, load them again first: the combo-set groups
    // below are taken from the rows, and a stale list must not be saved into the plan. If
    // they still cannot be loaded, nothing is saved and the user keeps their edits.
    if (this.exercisesLoadFailed && !await this.withLoading(() => this.reloadAllWorkoutExercisesAsync())) {
      if (this.viewDestroyed) return false;
      await this.alertCtrl.showAlertAsync({
        title: LL.editWorkouts.exercisesLoadFailed.title(),
        message: LL.editWorkouts.exercisesLoadFailed.saveRefused(),
        buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary }],
      });
      return false;
    }

    // ensure comboSetGroups are up to date for all workouts
    for (const workout of this.workouts) {
      const exercises = this.workoutExercises.get(workout.name);
      if (exercises) {
        this.updateComboSetGroups(workout, exercises);
      }
    }

    if (this.mode === 'intake') {
      // intake mode: update snapshots for change detection
      this.fitnessPlanOrg = deepClone(this.fitnessPlan);
      this.fitnessStateOrg = deepClone(this.fitnessState);
      return true;
    }

    // edit mode: save to database
    // remember before saving (comparison uses Org snapshots)
    const needsScheduleUpdate = this.scheduleUpdateRequired();

    // #6428: a coach's changes to single exercises. The history the deload rule needs is read
    // before anything is written; a failed read saves nothing and keeps the edits
    let coachHistories: Map<string, RecentExerciseHistory>;
    try {
      coachHistories = await this.readCoachHistoriesAsync();
    } catch (err) {
      this.logging.trackSilentProblem('edit-workouts.coach-history-unreadable', { error: describeErrorForLog(err) });
      if (this.viewDestroyed) return false;
      await this.alertCtrl.showAlertAsync({
        title: LL.editWorkouts.exercisesLoadFailed.title(),
        message: LL.editWorkouts.exercisesLoadFailed.saveRefused(),
        buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary }],
      });
      return false;
    }

    const transaction = DatabaseWrapper.getTransactionId();

    // create backup before saving
    await this.userDb.createCurrentPlanBackupLog(transaction);

    // merge working changes back to canonical objects, and hand each save the object
    // commitDraft() wrote into, so the edits survive the database being re-opened under
    // this page — see design-docs/sync-integrity.md, "A third door" (df-224ad165d843)
    // #6428: the deload rule, the cap-skip marks and the stamp go onto the drafts before they are committed
    const coachDocs = this.prepareCoachChanges(coachHistories);
    const committedPlan = this.fitnessPlanDraft.commitDraft();
    const committedState = this.fitnessStateDraft.commitDraft();

    // save the fitness plan and state
    await this.userDb.savePlanAsync(transaction, committedPlan);
    await this.userDb.saveFitnessStateAsync(transaction, committedState);
    // the records last: a failed one is reported there and the plan save stands
    await this.coachExerciseChange.recordAsync(coachDocs, transaction);
    this.coachEdits.clear();

    // clear backlog cache only if workout structure changed
    if (needsScheduleUpdate) {
      await this.userDb.clearBacklogCacheAsync();
    }

    // update snapshots after save
    this.fitnessPlanOrg = deepClone(this.fitnessPlan);
    this.fitnessStateOrg = deepClone(this.fitnessState);
    return true;
  }

  /**
   * #6428: the recent history of every program entry a coach changed, for the deload rule.
   * Throws when a read fails, so the caller saves nothing.
   */
  private async readCoachHistoriesAsync(): Promise<Map<string, RecentExerciseHistory>> {
    const histories = new Map<string, RecentExerciseHistory>();
    for (const subSlotId of this.coachEdits.keys()) {
      const exerciseId = this.fitnessPlan.subSlots?.[subSlotId]?.exercise_id;
      if (!exerciseId || exerciseId !== this.fitnessPlanOrg.subSlots?.[subSlotId]?.exercise_id) continue;
      histories.set(subSlotId, await this.coachExerciseChange.readHistoryAsync(exerciseId, this.fitnessPlan));
    }
    return histories;
  }

  /**
   * #6428: runs the shared save step for each program entry a coach changed, writes its outcome
   * onto the draft, and answers the change records to write after the save. One timestamp for the
   * whole save. An entry removed or given another exercise since gets nothing.
   */
  private prepareCoachChanges(histories: Map<string, RecentExerciseHistory>): ExerciseChangeDoc[] {
    const docs: ExerciseChangeDoc[] = [];
    const changed_utc = this.debug.getNowLocal().toUTC().toISO();
    for (const [subSlotId, { pathsRebuiltBySwitch, volume }] of this.coachEdits) {
      const original = this.fitnessPlanOrg.subSlots?.[subSlotId];
      const edited = this.fitnessPlan.subSlots?.[subSlotId];
      if (!original || !edited || original.exercise_id !== edited.exercise_id) continue;
      const setAsideId = setAsideExerciseIdOf(original);
      const prepared = prepareChange({
        subSlotId,
        original: {
          subSlot: original,
          stat: this.fitnessStateOrg.exerciseStats?.[original.exercise_id],
          setAsideStat: setAsideId ? this.fitnessStateOrg.exerciseStats?.[setAsideId] : undefined,
        },
        edited: {
          subSlot: edited,
          stat: this.fitnessState.exerciseStats?.[edited.exercise_id],
          setAsideStat: setAsideId ? this.fitnessState.exerciseStats?.[setAsideId] : undefined,
        },
        pathsRebuiltBySwitch,
        volume,
        historyNewestFirst: histories.get(subSlotId)?.entriesNewestFirst ?? [],
        bodyWeight_kg: this.fitnessState.weight_kg,
        exerciseBw_pc: bodyWeightShareFromSessions(histories.get(subSlotId)?.sessionsNewestFirst),
        changed_utc,
        author: this.coachExerciseChange.author(),
      });
      applyPreparedChange(edited, prepared);
      if (prepared.doc) docs.push(prepared.doc);
    }
    return docs;
  }

  /** #6268: ask what to do with any workout day that has no exercises, before saving.
   *
   *  Edit Workouts makes such a day two ways: the "+" tab adds one empty, and removing a
   *  day's last exercise is allowed while another day still has some (the #4587 guards
   *  count the whole program). The training cycle schedules every day in turn, so a saved
   *  empty day came round as a blank workout on the Training page.
   *
   *  Answers true when the save may go on: there was no empty day, or the user chose to
   *  delete them (which loses nothing). Answers false when the user chose to keep editing. */
  private async resolveEmptyWorkoutsBeforeSaveAsync(): Promise<boolean> {
    const emptyWorkouts = findEmptyWorkouts(this.fitnessPlan);
    if (emptyWorkouts.length === 0) return true;

    // if EVERY day is empty, this is the whole-program case #4587 owns: it is allowed for
    // somebody who trains no days, and otherwise already flagged for a rebuild. Deleting
    // days cannot fix it either, since one day must always remain.
    if (emptyWorkouts.length === this.workouts.length) return true;

    // show the (first) empty day behind the alert, so the user sees what it is about
    this.selectedWorkoutName = emptyWorkouts[0].name;
    this.cdr.markForCheck();

    const single = emptyWorkouts.length === 1;
    const names = emptyWorkouts.map(w => w.name).join(', ');
    // past workouts store a day's position, and deleting a day in the middle renames the
    // days after it, which would relabel them — so Delete is offered only when the empty
    // days are all at the end. Otherwise the user adds an exercise, or deletes the day
    // themselves with the Delete Workout button and its own confirmation.
    const deleteButton = emptyWorkoutsAreAllAtTheEnd(this.fitnessPlan) ? [{
      text: single
        ? LL.editWorkouts.buttons.deleteWorkout({ name: emptyWorkouts[0].name })
        : LL.editWorkouts.emptyWorkout.deleteSeveral(),
      role: AlertButtonRole.Primary,
      result: 'delete' as const,
    }] : [];
    const choice = await this.alertCtrl.showAlertAsync<'delete' | false>({
      title: LL.editWorkouts.emptyWorkout.title(),
      message: single
        ? LL.editWorkouts.emptyWorkout.message({ name: emptyWorkouts[0].name })
        : LL.editWorkouts.emptyWorkout.messageSeveral({ names }),
      implicitCancelResult: false,
      buttons: [
        // a lone button must be Primary — the alert controller reports any other role as a problem
        {
          text: LL.unsavedChanges.buttons.keepEditing(),
          role: deleteButton.length ? AlertButtonRole.Cancel : AlertButtonRole.Primary,
          result: false,
        },
        ...deleteButton,
      ],
    });
    if (this.viewDestroyed) return false;
    if (choice !== 'delete') return false;

    // the days hold no exercises, so removing them loses nothing and needs no second question
    for (const workout of emptyWorkouts) {
      this.removeWorkout(workout);
    }
    return true;
  }

  hasUnsavedChanges(): boolean {
    return !deepCompareEQ(this.fitnessPlan, this.fitnessPlanOrg) ||
           !deepCompareEQ(this.fitnessState, this.fitnessStateOrg);
  }

  // check if changes require schedule recalculation (workout order/structure changed)
  private scheduleUpdateRequired(): boolean {
    return !deepCompareEQ(this.fitnessPlan.workouts, this.fitnessPlanOrg.workouts);
  }

  async ionViewCanLeave(): Promise<CanLeaveResult<EditWorkoutsPageResult>> {
    const result = await this.unsavedChangesGuard.confirmLeaveAsync();
    // track back-button usage during intake (only if user confirmed leaving, not canceled)
    if (this.mode === 'intake' && !this.exitViaSave && result !== false) {
      this.telemetry.trackEvent('edit_workouts_back_during_intake', {
        hasChanges: !deepCompareEQ(this.fitnessPlan, this.fitnessPlanOrg),
      });
    }
    return result;
  }

  async onSaveAndExitClick() {
    this.exitViaSave = true;
    // if the save was declined, the user is still here and did not leave via Save
    if (!await this.unsavedChangesGuard.saveAndExitAsync()) {
      this.exitViaSave = false;
    }
  }

  async onDeleteWorkoutClick() {
    const workout = this.getSelectedWorkout();
    if (!workout) return;

    // #4587: deleting a workout takes its exercises with it, so it is the OTHER way to
    // leave the plan with nothing to train. Adding an empty workout and then deleting the
    // one holding the exercises passes the "at least one workout" check just below, which
    // counts days rather than exercises — so both guards are needed, not either one.
    if (!this.mayLeavePlanWithNoExercises()
        && this.planWouldHaveNoExercisesAfter({ dropWholeWorkout: workout })) {
      await this.warnPlanNeedsAnExerciseAsync();
      return;
    }

    // prevent deleting the last workout
    if (this.workouts.length <= 1) {
      await this.alertCtrl.showAlertAsync({
        title: LL.editWorkouts.deleteWorkout.cannotDeleteTitle(),
        message: LL.editWorkouts.deleteWorkout.cannotDeleteMessage(),
        buttons: [
          { text: LL.common.buttons.ok(), role: AlertButtonRole.Primary, result: true },
        ],
      });
      return;
    }

    // show confirmation dialog
    const confirmed = await this.alertCtrl.showAlertAsync({
      title: LL.editWorkouts.deleteWorkout.confirmTitle(),
      message: LL.editWorkouts.deleteWorkout.confirmMessage({
        name: workout.name
      }),
      buttons: [
        { text: LL.common.buttons.cancel(), role: AlertButtonRole.Cancel, result: false },
        { text: LL.common.buttons.delete(), role: AlertButtonRole.Primary, result: true },
      ],
    });

    if (!confirmed) return;

    this.removeWorkout(workout);
  }

  /** Remove one workout day from the draft: its now-unused exercises, its cached rows, and
   *  rename the days after it so the letters stay A, B, C, ... Asks nothing — callers ask. */
  private removeWorkout(workout: Plan_Workout) {
    // find the index of the workout to delete
    const workoutIndex = this.workouts.indexOf(workout);
    if (workoutIndex < 0) return;

    // remove the workout from the array
    this.workouts.splice(workoutIndex, 1);

    // clean up subSlots that were only used by this workout
    for (const subSlotId of workout.subSlotIds || []) {
      // check if any other workout uses this subSlot
      const usedElsewhere = this.workouts.some(w =>
        w.subSlotIds?.includes(subSlotId)
      );
      if (!usedElsewhere) {
        delete this.fitnessPlan.subSlots[subSlotId];
      }
    }

    // clear the cached exercises for the deleted workout
    this.workoutExercises.delete(workout.name);

    // rename remaining workouts to keep sequential naming (A, B, C, ...)
    const workoutNames = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    for (let i = 0; i < this.workouts.length; i++) {
      const oldName = this.workouts[i].name;
      const newName = workoutNames[i];
      if (oldName !== newName) {
        this.workouts[i].name = newName;
        // update the workoutExercises cache with new name
        const exercises = this.workoutExercises.get(oldName);
        if (exercises) {
          this.workoutExercises.delete(oldName);
          this.workoutExercises.set(newName, exercises);
        }
      }
    }

    // select the previous workout, or the first one if we deleted the first
    const newIndex = Math.max(0, workoutIndex - 1);
    this.selectedWorkoutName = this.workouts[newIndex].name;

    this.cdr.markForCheck();
  }

  trackByWorkout(_index: number, workout: Plan_Workout): string {
    return workout.name;
  }

  getSelectedWorkout(): Plan_Workout | undefined {
    return this.workouts.find(w => w.name === this.selectedWorkoutName);
  }

  getExercisesForWorkout(workoutName: string): UiExercise[] {
    return this.workoutExercises.get(workoutName) || [];
  }

  async onExerciseMoreOptionsClick(event: { event: Event, exercise: UiExercise }) {
    event.event.stopPropagation();
    const targetElement = event.event.target as HTMLElement;
    const exercise = event.exercise;

    const currentWorkout = this.getSelectedWorkout();
    const otherWorkouts = this.workouts.filter(w => w.name !== currentWorkout?.name);

    const buttons: PopupMenuButton[] = [
      {
        text: LL.workouts.buttons.changeExercise(),
        handler: () => this.handleChangeExercise(exercise)
      },
      {
        text: LL.editWorkouts.menu.removeExercise(),
        handler: () => void this.handleRemoveExercise(exercise)
      },
      {
        text: LL.editWorkouts.menu.moveExercise(),
        submenu: {
          title: LL.editWorkouts.menu.moveExerciseTo(),
          buttons: otherWorkouts.map(workout => ({
            text: LL.editWorkouts.menu.workoutName({ name: workout.name }),
            handler: () => void this.moveExerciseToWorkout(exercise, currentWorkout!, workout)
          }))
        }
      },
      {
        text: LL.workouts.buttons.instructions(),
        handler: () => this.handleExerciseInstructions(exercise)
      },
      {
        text: LL.workouts.buttons.history(),
        handler: () => this.handleExerciseHistory(exercise)
      },
      {
        text: LL.workouts.buttons.settings(),
        handler: () => this.handleExerciseSettings(exercise)
      },
    ];

    await this.popupMenu.createMenu({
      targetFromElement: targetElement,
      buttons,
    });
  }

  private handleChangeExercise(exercise: UiExercise) {
    const workout = this.getSelectedWorkout();
    if (!workout) return;

    const subSlotId = this.getSubSlotIdForExercise(exercise);
    if (!subSlotId) return;

    // get workout reference (index in workouts array)
    const workoutReference = this.workouts.indexOf(workout);

    void this.navCtrl.pushAsync(IExerciseSwitchPage, {
      exerciseId: exercise.exercise._id,
      subSlotId: subSlotId,
      workoutId: workout.name,
      returnCallback: 'onExerciseSwitched',
      warnBeforeLinkingWorkouts: true,
      local: {
        fitnessPlan: this.fitnessPlan,
        fitnessState: this.fitnessState,
        returnContext: {
          action: 'change',
          workoutName: workout.name,
          workoutReference,
          subSlotId,
          oldExerciseId: exercise.exercise._id,
        },
      },
    });
  }

  // Resolve the subslot to swap against the LIVE plan. The subslot id captured when the picker
  // opened can be stale — an app reload mid-edit, or a fork/rename that re-keyed the subslot.
  // Returns the id still referenced by this workout that holds the exercise being swapped, or
  // undefined if that exercise is genuinely no longer in this workout.
  private resolveLiveSwapSubSlotId(
    workout: Plan_Workout,
    capturedSubSlotId: string | undefined,
    oldExerciseId: string | undefined,
  ): string | undefined {
    // if we don't know which exercise to swap, there is nothing to resolve against —
    // guard so we never match a subslot that happens to have an undefined exercise_id
    if (!oldExerciseId) return undefined;
    // does this subslot id still point at the exercise we mean to swap?
    const subSlotHasOldExercise = (id: string) => this.fitnessPlan.subSlots[id]?.exercise_id === oldExerciseId;
    // if the captured id is still valid in this workout, keep it
    if (capturedSubSlotId && workout.subSlotIds.includes(capturedSubSlotId) && subSlotHasOldExercise(capturedSubSlotId)) {
      return capturedSubSlotId;
    }
    // otherwise re-resolve to whichever subslot in this workout now holds the exercise
    return workout.subSlotIds.find(subSlotHasOldExercise);
  }

  async onExerciseSwitched(
    context: {
      action: 'add' | 'change',
      workoutName: string,
      workoutReference: number,
      subSlotId?: string,
      oldExerciseId?: string,
      insertAtIndex?: number,
    },
    newExerciseId: string
  ) {
    // if user cancelled (no exercise selected), do nothing
    if (!newExerciseId) return;

    const workout = this.workouts.find(w => w.name === context.workoutName);
    if (!workout) return;

    await this.withLoading(async () => {
      // Fork / revert in-place rename detection. Edit-exercise's fork branch and the
      // picker's fork-Delete handler mutate the userDb singleton's subSlot.exercise_id
      // directly (they preserve exercisePaths, lastTimeConcluded_utc, etc. — a rename,
      // not a swap) and save. This page holds its own CouchDocDraft, which doesn't see
      // the singleton mutation. Without this guard we fall through to the swap below,
      // which sees a stale draft (oldExerciseId from draft != newExerciseId from picker)
      // and treats it as a genuine swap — creates a fresh subSlot under slot/$custom with
      // empty exercisePaths, dropping the rep-target preference and progression state
      // (PRD #270). Detect by comparing the singleton's subSlot.exercise_id against the
      // newExerciseId; if they match, the rename already happened upstream — sync the
      // draft from the singleton and skip the destructive switch path.
      if (context.action === 'change' && context.subSlotId) {
        const singletonSubSlot = this.userDb.fitnessPlan?.subSlots?.[context.subSlotId];
        if (singletonSubSlot?.exercise_id === newExerciseId) {
          const subSlotClone = deepClone(singletonSubSlot);
          this.fitnessPlan.subSlots[context.subSlotId] = subSlotClone;
          const migratedStat = this.userDb.fitnessState.exerciseStats?.[newExerciseId];
          if (migratedStat) {
            this.fitnessState.exerciseStats[newExerciseId] = deepClone(migratedStat);
          }
          // narrow snapshot refresh to the synced fields only — wholesale Org refresh
          // would silently swallow OTHER in-flight unsaved Edit Workouts edits (reorders,
          // combo-group changes, removals on different exercises) and bypass the
          // unsavedChangesGuard on page exit
          if (this.fitnessPlanOrg?.subSlots) {
            this.fitnessPlanOrg.subSlots[context.subSlotId] = deepClone(subSlotClone);
          }
          if (migratedStat && this.fitnessStateOrg?.exerciseStats) {
            this.fitnessStateOrg.exerciseStats[newExerciseId] = deepClone(migratedStat);
          }
          // the replaced subslot may also be listed on other workouts — rebuild every tab's rows,
          // or those tabs keep showing the exercise as it was before the rename
          await this.refreshAfterPlanChangeAsync();
          return;
        }
      }

      // use canonical plan's exercise_id — the context's oldExerciseId may be stale
      // if the plan changed while the exercise-switch page was open
      const oldExerciseId = context.subSlotId
        ? (this.fitnessPlan.subSlots[context.subSlotId]?.exercise_id ?? context.oldExerciseId)
        : context.oldExerciseId;

      // the plan edit below mutates this.fitnessPlan.workouts[workoutReference]. Derive the
      // index from the name-resolved workout — NOT the captured context.workoutReference, which
      // can drift if the plan reloaded under the picker — so validation, the swap itself, and the
      // post-swap UI reload all act on the same workout object, for both add and change.
      const workoutReference = this.fitnessPlan.workouts.indexOf(workout);
      if (workoutReference < 0) {
        // the workout the user was editing is no longer in the live plan (a reload dropped it) —
        // surface and bail rather than mutating plan.workouts[-1] (undefined → uncaught throw).
        this.logging.trackSilentProblem('edit-workouts.switch-missing-workout', {
          workoutName: context.workoutName,
          workoutReference: context.workoutReference,
          action: context.action,
          totalWorkouts: this.fitnessPlan.workouts.length,
        });
        return;
      }

      // for a swap, re-resolve the captured subslot against the live workout — it can go stale
      // (an app reload mid-edit, or a fork/rename that re-keyed it). If the exercise is genuinely
      // gone, surface and bail rather than letting changeExerciseInWorkoutAsync throw an uncaught "Old
      // subslot ... not found in workout" (Sentry Group 8 / 3XN).
      let effectiveSubSlotId = context.subSlotId;
      if (context.action === 'change') {
        effectiveSubSlotId = this.resolveLiveSwapSubSlotId(workout, context.subSlotId, oldExerciseId);
        if (!effectiveSubSlotId) {
          this.logging.trackSilentProblem('edit-workouts.swap-stale-subslot', {
            subSlotId: context.subSlotId,
            oldExerciseId,
            newExerciseId,
            workoutName: context.workoutName,
            workoutReference,
          });
          return;
        }
      }

      // go through the plan-edit functions so progression backup and subslot management are
      // handled properly. Adding and swapping are two different edits taking different
      // arguments, so which one runs is decided here rather than inside them (df-c9ee6db39869).
      const { newSubslotId } = context.action === 'add'
        ? await addExerciseToWorkoutAsync(
          workoutReference,
          newExerciseId,
          this.fitnessPlan,
          this.fitnessState,
          this.exerciseDb,
          this.userDb,
          this.debug.getNowLocal(),
          this.docWatcher
        )
        : await changeExerciseInWorkoutAsync(
          workoutReference,
          effectiveSubSlotId,
          oldExerciseId,
          newExerciseId,
          this.fitnessPlan,
          this.fitnessState,
          this.exerciseDb,
          this.userDb,
          this.debug.getNowLocal(),
          this.docWatcher,
          ExerciseChosenBy.User,
        );

      // if adding at a specific position, move from end to correct index
      if (context.action === 'add' && context.insertAtIndex !== undefined && newSubslotId) {
        // the add puts the subslot at the end, move to correct position
        const currentIndex = workout.subSlotIds.indexOf(newSubslotId);
        if (currentIndex >= 0 && currentIndex !== context.insertAtIndex) {
          // remove from current position
          workout.subSlotIds.splice(currentIndex, 1);
          // insert at desired position
          workout.subSlotIds.splice(context.insertAtIndex, 0, newSubslotId);
        }
      }

      // reload the rows of EVERY workout (rebuilds editDescription, etc.), not just this one: a
      // same-slot swap replaces plan.subSlots[id] under the same id, and that id can be listed on
      // other workouts too. A row left unrebuilt there shows the swapped-away exercise, and its
      // Remove confirmation names an exercise the slot no longer holds.
      await this.refreshAfterPlanChangeAsync();
    });
  }

  private async handleRemoveExercise(exercise: UiExercise) {
    const workout = this.getSelectedWorkout();
    if (!workout) return;

    // #4587: refuse to remove the plan's LAST exercise, the same way deleting the last
    // workout is refused just below. A built plan holding no exercises produces nothing
    // but blank workouts, and the repair in `UserDb.loadPlanAsync` cannot tell one the
    // user emptied on purpose from one that lost its exercises to a bug — so the app
    // stops the state being reachable by hand instead of guessing at intent later.
    // Somebody who trains no days a week is the one person an empty plan suits, and for
    // them it is allowed (owner's rule) — the same condition the repair itself uses.
    const subSlotId = this.getSubSlotIdForExercise(exercise);
    if (subSlotId && !this.mayLeavePlanWithNoExercises()
        && this.planWouldHaveNoExercisesAfter({ fromWorkout: workout, dropSubSlotId: subSlotId })) {
      await this.warnPlanNeedsAnExerciseAsync();
      return;
    }

    const confirmed = await this.alertCtrl.showAlertAsync({
      title: LL.exerciseSwitch.removeExercise.title(),
      message: LL.exerciseSwitch.removeExercise.message({
        exercise: exercise.exercise.name,
        workout: workout.name
      }),
      buttons: [
        { text: LL.common.buttons.cancel(), role: AlertButtonRole.Cancel, result: false },
        { text: LL.common.buttons.remove(), role: AlertButtonRole.Primary, result: true },
      ],
    });

    if (confirmed) {
      await this.removeExerciseFromWorkout(exercise, workout);
    }
  }

  private async moveExerciseToWorkout(
    exercise: UiExercise,
    fromWorkout: Plan_Workout,
    toWorkout: Plan_Workout
  ) {
    const subSlotId = this.getSubSlotIdForExercise(exercise);
    if (!subSlotId) return;

    // Move the exercise, then rebuild the UI from the changed plan before triggering change
    // detection. This goes through the one refresh step every plan change uses, which rebuilds
    // EVERY workout's rows rather than only the two involved here. That is a superset of what
    // this move strictly needs; it is taken deliberately, so that this site is not a special
    // case somebody has to re-derive the next time the move changes.
    await this.applyPlanChangeAsync(() => moveSubslotToWorkout(fromWorkout, toWorkout, subSlotId));

    // show toast with link to target workout
    this.toastCtrl.showToast({
      message: LL.editWorkouts.toast.exerciseMoved({ workout: toWorkout.name }),
      messageLink: LL.editWorkouts.toast.view(),
      onMessageLink: () => {
        this.selectedWorkoutName = toWorkout.name;
        this.cdr.markForCheck();
      },
      duration: 5000,
    });
  }

  private handleExerciseInstructions(exercise: UiExercise) {
    void this.navCtrl.pushAsync(IExerciseStatsPage, {
      id: exercise.exercise._id,
      mode: 'instructions'
    });
  }

  private handleExerciseHistory(exercise: UiExercise) {
    void this.navCtrl.pushAsync(IExerciseStatsPage, {
      id: exercise.exercise._id,
      mode: 'log'
    });
  }

  private handleExerciseSettings(exercise: UiExercise) {
    void this.navCtrl.pushAsync(IExerciseStatsPage, {
      id: exercise.exercise._id,
      mode: 'settings'
    });
  }

  onExerciseHeaderClick(exercise: UiExercise) {
    const subSlotId = this.getSubSlotIdForExercise(exercise);
    if (!subSlotId) return;

    void this.navCtrl.pushAsync(IExerciseConfigurationPage, {
      subSlotId,
      returnCallback: 'onConfigurationResult',
      // #6428: only the edit mode saves the coach's parts; the intake mode saves nothing itself
      coachEditable: this.mode === 'edit',
      local: {
        fitnessPlan: deepClone(this.fitnessPlan),
        fitnessState: deepClone(this.fitnessState),
      },
    });
  }

  async onConfigurationResult(_context: unknown, result: ExerciseConfigurationPageResult) {
    // if page was closed without explicit save/cancel (e.g. header back button), treat as no changes
    if (!result) return;

    // #6428: remember a coach's edit; a switch in any visit means the paths were rebuilt
    if (result.saved && result.coachEdit) {
      const id = result.coachEdit.subSlotId;
      const earlier = this.coachEdits.get(id);
      // the count shown before the first visit, and the count left by the latest one that changed it
      const before = earlier?.volume?.before ?? result.coachEdit.volume?.before;
      const after = result.coachEdit.volume?.after ?? earlier?.volume?.after;
      this.coachEdits.set(id, {
        pathsRebuiltBySwitch: (earlier?.pathsRebuiltBySwitch ?? false) || result.coachEdit.pathsRebuiltBySwitch,
        volume: before !== undefined && after !== undefined && before !== after ? { before, after } : undefined,
      });
    }

    // if the page made changes, update our local state
    if (result.modified) {
      await this.withLoading(async () => {
        // update in-place to preserve references for unsaved changes guard
        deepOverwriteCouchSafe(this.fitnessPlan, result.fitnessPlan);
        deepOverwriteCouchSafe(this.fitnessState, result.fitnessState);

        // The overwrite above replaced the whole plan's contents, so EVERY cached display row
        // still describes the plan as it was before the configuration page changed it. Rebuild
        // ALL workouts' rows, not just the selected one: the same subslot can appear on more
        // than one workout, and a row left unrebuilt shows stale settings (#4689).
        //
        // The muscle-volume figures have to be rebuilt here too, and used not to be
        // (`df-44aded267ee8`). The configuration page writes the plan's set count, which is a
        // direct input to the volume figures — so returning from it used to leave this page
        // holding figures for the plan as it was before the edit, and the Muscle Volume page
        // opened from here is seeded from exactly those figures.
        await this.refreshAfterPlanChangeAsync();
      });
    }
  }

  async onExerciseAddConfigResult(
    context: { newSubslotId: string, workoutName?: string, workoutReference: number, insertAtIndex?: number },
    result: ExerciseConfigurationPageResult
  ) {
    // if cancelled, real plan was never touched — nothing to do
    if (!result?.saved) return;

    await this.withLoading(async () => {
      // Resolve the target workout from this.fitnessPlan.workouts — the SAME object the dict
      // write below mutates — by stable workout name, so the dict entry and its workout
      // reference can never land on different plan instances. The previous code read
      // this.workouts[index]; on return from the picker the page can reload (ionViewWillEnter →
      // loadAsync rebuilds the draft), and the index/array could drift, orphaning the new
      // subslot (Sentry Group 8 / 44C). Fall back to the index only when an older nav context
      // carries no name.
      const workout = context.workoutName !== undefined
        ? this.fitnessPlan.workouts.find(w => w.name === context.workoutName)
        : this.fitnessPlan.workouts[context.workoutReference];
      const newSubSlot = result.fitnessPlan.subSlots[context.newSubslotId];

      // If the target workout or the configured subslot is gone, do NOT write a half state (a
      // dict entry with no workout reference): savePlanAsync's orphan heal would silently
      // destroy it and the exercise the user just added would vanish. Surface and abort instead
      // (CLAUDE.md: unexpected conditions must surface).
      if (!workout || !newSubSlot) {
        this.logging.trackSilentProblem('edit-workouts.add-config-missing-target', {
          newSubslotId: context.newSubslotId,
          workoutName: context.workoutName,
          workoutReference: context.workoutReference,
          hasWorkout: !!workout,
          hasSubSlot: !!newSubSlot,
          totalWorkouts: this.fitnessPlan.workouts.length,
        });
        return;
      }

      // copy the configured subSlot (has user's sets/rep target edits) and add its workout
      // reference in the same step, so the dict entry never exists without a reference.
      this.fitnessPlan.subSlots[context.newSubslotId] = newSubSlot;
      // also integrate fitnessState changes — Phase 2 moves the user's stated rep-target preference
      // onto ExerciseStat.preferRepTargetByIntensity (in fitnessState), so the exercise-configuration
      // page may have written there. Without this overwrite the preference would be silently dropped
      // when edit-workouts saves only fitnessPlan. Mirrors onConfigurationResult above.
      deepOverwriteCouchSafe(this.fitnessState, result.fitnessState);
      // add to workout at the right position — guard against a duplicate id. The added exercise
      // can resolve to a subslot the workout already references, which would push a duplicate
      // (Sentry Group 8 / 456). Mirrors the #3483 guard in addExerciseToWorkoutAsync.
      if (!workout.subSlotIds.includes(context.newSubslotId)) {
        if (context.insertAtIndex !== undefined) {
          workout.subSlotIds.splice(context.insertAtIndex, 0, context.newSubslotId);
        } else {
          workout.subSlotIds.push(context.newSubslotId);
        }
      }

      // reload every workout's rows to reflect the addition: the subslot written above may be one
      // another workout already lists, and fitnessState was overwritten, so rows on other tabs can
      // be stale too
      await this.refreshAfterPlanChangeAsync();
    });
  }

  /** Which sub-slot does this display row belong to? The row wrote its id down when it was
   *  built, so this survives the plan's contents being replaced wholesale — searching the
   *  plan for the row's cached object did not, which is #4689.
   *
   *  Answers `undefined` when the program holds no entry under that id — the exercise was
   *  removed, or a swap re-keyed it. Every caller handles that. */
  private getSubSlotIdForExercise(exercise: UiExercise): string | undefined {
    const subSlotId = exercise.planSubSlotId;
    // if the plan still holds an entry under this row's id, that id is the answer
    if (subSlotId && this.fitnessPlan.subSlots[subSlotId]) {
      return subSlotId;
    }
    return undefined;
  }

  /** #4587: would the plan still hold an exercise after this removal?
   *
   *  Counts over EVERY workout, not just the one on screen: what empties the plan is
   *  removing the last exercise of the last non-empty day, and that day is not necessarily
   *  the selected one. Pass `dropWholeWorkout` to ask the question for deleting a workout,
   *  or `dropSubSlotId` to ask it for removing one exercise from `fromWorkout`.
   *
   *  A subSlotId two workouts share still leaves the plan populated when only one of the
   *  two references goes, which is why this counts surviving references rather than ids. */
  private planWouldHaveNoExercisesAfter(
    options: { dropWholeWorkout?: Plan_Workout; fromWorkout?: Plan_Workout; dropSubSlotId?: string },
  ): boolean {
    for (const w of this.workouts) {
      if (w === options.dropWholeWorkout) continue;
      for (const id of w.subSlotIds ?? []) {
        if (w === options.fromWorkout && id === options.dropSubSlotId) continue;
        return false;
      }
    }
    return true;
  }

  /** #4587: may the user leave the plan with no exercises at all? Only somebody who trains
   *  no days a week may, because for them an empty plan is what they asked for (owner's
   *  rule) — the same condition `planIsBuiltButHasNoExercises` applies on the load side.
   *
   *  Answers no when the declared state has not loaded. `getWorkoutsPerWeek` reports 0 for
   *  a missing `trainingDays`, and 0 is the one value that switches this protection off —
   *  so trusting it raw would turn "we do not know yet" into "this user does not train"
   *  and drop the guard exactly when the page knows least. Refusing is the safe direction:
   *  the worst it costs is that somebody who genuinely trains no days is asked to swap an
   *  exercise instead of removing it. */
  private mayLeavePlanWithNoExercises(): boolean {
    return !!this.declaredState?.trainingDays && getWorkoutsPerWeek(this.declaredState) === 0;
  }

  /** #4587: the alert both guards show when they refuse. */
  private async warnPlanNeedsAnExerciseAsync(): Promise<void> {
    await this.alertCtrl.showAlertAsync({
      title: LL.editWorkouts.removeExercise.cannotRemoveLastTitle(),
      message: LL.editWorkouts.removeExercise.cannotRemoveLastMessage(),
      buttons: [
        { text: LL.common.buttons.ok(), role: AlertButtonRole.Primary, result: true },
      ],
    });
  }

  private async removeExerciseFromWorkout(exercise: UiExercise, workout: Plan_Workout) {
    const subSlotId = this.getSubSlotIdForExercise(exercise);
    if (!subSlotId) return;

    // remove from workout.subSlotIds, and drop the now-unreferenced plan.subSlots entry
    // unless another workout still uses the same subSlotId (#3320 invariant).
    removeSubslotFromWorkout(this.fitnessPlan, workout, subSlotId);

    // remove from comboSetGroups
    if (workout.comboSetGroups) {
      delete workout.comboSetGroups[subSlotId];
    }

    // update UI
    const exercises = this.workoutExercises.get(workout.name) || [];
    this.workoutExercises.set(workout.name, exercises.filter(e => e !== exercise));
    await this.refreshMuscleVolumeAsync();
    this.cdr.markForCheck();
  }
}
